/**
 * search.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs/Rx";

import { Algorithm } from "../algorithms/structure/algorithm";
import { AlertService } from "../common/alert.service";
import { TypedData } from "../common/data/typed-data";
import { MessageReceiver } from "../server/message-receiver";
import { AcknowledgmentResponse, Message, MessageTag, ResponseCode }
    from "../server/messages/general/";
import {
    AdditionalSearchResultsRequest,
    SubjectContextSearchRequest,
    SubjectContextSearchResponse,
    SubjectContextSearchResultRequest,
    SubjectContextSearchTreeResponse,
} from "../server/messages/search/";
import { MessagingService } from "../server/messaging.service";
import { Subject } from "../subjects/subject";
import { SubjectContextType } from "../subjects/subject-context-type";
import { SubjectReference } from "../subjects/subject-reference";
import { SubjectService } from "../subjects/subject.service";
import { disconnectEmpty, disconnectValue, disconnectWith }
    from "../support/types/decorators";
import { oneline, assert } from "../support/utilities/functions";
import { RingBuffer } from "../support/utilities/ring-buffer";

import {
    FilterNode,
    MessageSearchNode,
    MessageSearchTree,
    PartitionNode,
    SearchNode,
    SearchRoot,
    SearchTerm
} from "./search-node";
import { ModalService } from "app/common/modal.service";
import { StorageService } from "app/support/APIs/storage.service";
import { MAGIC_OPS_BY_NAME, MagicOperator, PastXDays } from "./operators";

export interface ResultColumn
{
  name : string;
  value : any;
  type : string; // TODO: real type
}

export interface Result
{
  recordId : number;
  columns : ResultColumn[];
}

const ADDL_COUNT_RESOLUTION : number = 10;

/**
 * Convenient label for the type of a "recent query", which is a tuple of:
 *  - a {@link SubjectContextType} target
 *  - a successful query path within the search tree
 *  - the number of results found by the query
 *  - the last time the query was run
 *  - whether there were additional results
 */
export type HistoricalSearch = [
    SubjectContextType,
    SearchTerm[],
    number,
    Date,
    boolean
];

export interface SavedSearch {
  name : string;
  type : SubjectContextType;
  terms : SearchTerm[];
}

/**
 * The {@link SearchService} handles search configuration, search query
 * construction, execution of searches, and display of search results and
 * related metadata.
 */
@Injectable()
export class SearchService implements MessageReceiver
{
    /**
     * Map from {@link SubjectContextType} to the root node of all the trees
     * for that type.
     */
    @disconnectWith("clear")
    public rootByType : Map<SubjectContextType, SearchRoot> =
        new Map<SubjectContextType, SearchRoot>();

    /**
     * The {@link SubjectContextType}s that can be searched on.
     */
    @disconnectEmpty()
    public availableTypes : SubjectContextType[] = [];

    /**
     * Whether this client is actively executing a query.
     */
    @disconnectValue(false)
    public running : boolean = false;

    /**
     * The set of result items returned from the last run query.
     */
    @disconnectValue(null)
    public results : Result[] = null;

    /**
     * The subject context type of the current result set. Used to simplify the
     * item-lookup process (i.e. determining what algorithms are valid for the
     * current result set, since Result items don't know this information about
     * themselves).
     */
    @disconnectValue(null)
    public resultContextType : SubjectContextType = null;

    /**
     * Whether there may be additional results available for loading (because
     * there were more than the queryFetchSize×the number of loaded pages).
     *
     * Note this number may (??) be inaccurate when the total result count is an
     * exact multiple of the fetch size. I think I've observed it in the past
     * but haven't circled back to investigate yet. [TDHS]
     */
    @disconnectValue(false)
    public moreResultsAvailable : boolean = false;

    /**
     * The timestamp of when a query was first attempted (according to the
     * actions taken on this client).
     */
    @disconnectValue(null)
    public queryTime : Date = null;

    /**
     * The timestamp of when the last result set was received by this client.
     */
    @disconnectValue(null)
    public resultTime : Date = null;

    /**
     * Which item in the result set was opened last, if any.
     * Used by UI element(s) to emphasize the item as a navigational aid.
     */
    @disconnectValue(null)
    public lastResultOpened : Result | null = null;

    /**
     * The maximum number of items the server has indicated it will return in
     * one result message. Queries with more than this number of results will
     * require multiple "batches" and corrseponding request to retrieve.
     */
    @disconnectValue(null)
    public queryFetchSize : number | null;

    /**
     * Calculate the number of seconds the query took to run, from the POV of
     * this client.
     */
    public get executionSeconds () : number
    {
        return (+this.resultTime - +this.queryTime) / 1000;
    }

    /**
     * Retreive a human label for the number of available results.
     */
    public get resultCountLabel () : string
    {
        if (!Array.isArray(this.results) || this.running)
        {
            console.error(
                "UI requested result count label when results unavailable");
            return `〈in progress〉`;
        }
        if (this.moreResultsAvailable)
        {
            return oneline`
                more than ${this.queryFetchSize}
                records (${this.results.length} loaded)`;
        }
        else
        {
            return oneline`
                ${this.results.length}
                record${this.results.length === 1 ? "" : "s"}`;
        }
    }

    /**
     * A list of recently-executed queries, for the purposes of helping the
     * user return to a past result set quickly.
     */
    @disconnectWith("clear")
    public recentSearches : RingBuffer<HistoricalSearch> =
        new RingBuffer<HistoricalSearch>(5);

    public savedSearches : SavedSearch[] = [];
    public static readonly SavedSearchStorageKey : string = "saved_searches";

    /**
     * Whether the service is ready (i.e. connected and configured) for
     * regular operation.
     */
    @disconnectWith("next", false)
    public ready : BehaviorSubject<boolean> = new BehaviorSubject(false);

    constructor (
        protected messaging : MessagingService,
        protected alerts : AlertService,
        protected subjects : SubjectService,
        protected storage : StorageService,
        protected modals : ModalService
    )
    {
        this.messaging.ready.subscribe(
            (on : boolean) => on ? this.connected() : this.disconnected()
        );
    }

    /**
     * Initialization for when the {@link MessagingService} is ready.
     */
    public connected () : void
    {
      this.messaging.receive(MessageTag.RSP_SUBJECT_CONTEXT_SEARCH_TREE)
        .subscribe((message : SubjectContextSearchTreeResponse) => {
            this.rootByType.clear();
            this.availableTypes = [];
            message.searchTrees.forEach((treeType : MessageSearchTree) => {
                const root : SearchNode = SearchRoot.fromMessage(treeType.tree);
                this.rootByType.set(treeType.contextType, root);
                this.availableTypes.push(treeType.contextType);
            });
            this.queryFetchSize = message.queryFetchSize;
            this.ready.next(true);
        });
      this.savedSearches = this.storage.getWrappedValue(
        SearchService.SavedSearchStorageKey,
        [] as SavedSearch[],
        SearchService.SavedSearchExtractor
      );
    }

    private static SavedSearchExtractor (saveds : SavedSearch[]) : SavedSearch[]
    {
      return saveds.map(
        (s : SavedSearch) => {
          s.terms.forEach((t : SearchTerm) => {
            if (t.serializedMagicSource)
            {
              const op : MagicOperator<any> = MAGIC_OPS_BY_NAME.get(
                t.serializedMagicSource[0] as any);
              if (!op)
              {
                console.error(
                  `Couldn't find op for name ${t.serializedMagicSource[0]}`);
                return;
              }
              t.magicSource = [
                op,
                TypedData.deserialize({
                  value: t.serializedMagicSource[1],
                  type: op.argumentType
                })
              ];
            }
          });
          return s;
        });
    }

    private static SavedSearchCompressor (saveds : SavedSearch[])
      : SavedSearch[]
    {
      return saveds.map(
        (s : SavedSearch) => {
          s.terms.forEach((t : SearchTerm) => {
            if (t.magicSource)
            {
              t.serializedMagicSource = [
                t.magicSource[0].name,
                TypedData.serialize({
                  value: t.magicSource[1],
                  type: t.magicSource[0].argumentType
                })
              ];
              t.magicSource = undefined;
            }
          });
          return s;
        });
    }

    public saveSearch () : void
    {
      if (this.recentSearches.size() === 0)
      {
        return alert("No recent searches to save!");
      }
      const last : HistoricalSearch = this.recentSearches.last();
      this.modals.stringPrompt({
        title: "Template name",
        body: "Enter a name to save as this search template's title",
        placeholder: "My search"
      }).subscribe((name : string) => {
        const saved : SavedSearch = {
          name,
          type: last[0],
          terms: last[1]
        };
        this.savedSearches.push(saved);
        this.saveSaveds();
      });
    }

    public deleteSaved (index : number) : void
    {
      if (!(index in this.savedSearches))
      {
        return alert(`Could not find saved search with index ${index}`);
      }
      this.savedSearches.splice(index, 1);
      this.saveSaveds();
    }

    public runSaved (index : number) : void
    {
        const saved : SavedSearch = this.savedSearches[index];
        if (saved)
        {
            const remagickedQuery : SearchTerm[] =
              saved.terms.map((t : SearchTerm) => {
                if (t.magicSource)
                {
                  return t.magicSource[0].transform(t.name, t.magicSource[1]);
                }
                return t;
              });
            this.runQuery(saved.type, remagickedQuery);
        }
        else
        {
            throw new Error(`Could not find recent search with index ${index}`);
        }
    }

    private saveSaveds () : void
    {
      console.log("Setting saved", this.savedSearches);
      this.storage.setWrapped(
        SearchService.SavedSearchStorageKey,
        this.savedSearches,
        SearchService.SavedSearchCompressor
      );
    }

    /**
     * Teardown instructions for when the {@link MessagingService} has stopped.
     */
    public disconnected () : void {/* decorator generated */}

    public getRoot (type : SubjectContextType) : SearchRoot
    {
        if (this.rootByType.has(type))
        {
            return this.rootByType.get(type);
        }
        return SearchRoot.EMPTY;
    }

    public runQuery (
        type : SubjectContextType,
        terms : SearchTerm[],
        previousResultCount : number = -1
    ) : void
    {
        // Make a deep copy of the terms array
        const cachedTerms : SearchTerm[] =
        terms.map((term : SearchTerm) => {
            if (term.value instanceof Date) {
                return Object.assign({}, term, {value: new Date(term.value)});
            }
            else
            {
                return Object.assign({}, term);
            }
        });

        /**
         * Since the method receives a combined list of both search and filter
         * keys, we use the terms as a path and retrieve the path's endpoint,
         * then step back through parents until we find a PartitionNode.
         */
        let node : SearchNode = this.getRoot(type);
        let partitionCount : number = 0;
        terms.forEach((t : SearchTerm) => {
            node = node.retrieve([t.name]);
            if (!node)
            {
                console.error(terms, this.rootByType);
                throw new Error("Query did not match search tree structure");
            }
            if (node instanceof PartitionNode)
            {
                partitionCount++;
            }
            t.value = TypedData.serializeValueBy(t.value, node);
        });

        this.queryTime = new Date();
        this.lastResultOpened = null;
        this.running = true;
        this.messaging.converse(
            new SubjectContextSearchRequest(
                type,
                terms.slice(0, partitionCount),
                terms.slice(partitionCount)
            ), (error : AcknowledgmentResponse) => {
                if (error.code === ResponseCode.MissingData)
                {
                    this.alerts.warn(oneline`
                        The search encountered a data integrity issue and could
                        not run. This issue has automatically been logged by
                        the server.
                    `);
                }
                else
                {
                    this.alerts.warn(oneline`
                        Your search query could not run. (${error.reason}) This
                        is likely due to a problem with the application.
                    `);
                }
            }
        ).subscribe((msg : SubjectContextSearchResponse) => {
            this.results = msg.resultSet;
            this.resultContextType = type;
            this.resultTime = new Date();
            this.running = false;
            this.moreResultsAvailable = msg.canGetMoreData;
            if (msg.resultSet.length === previousResultCount)
            {
                console.info(
                    "Query received same number of results as last run");
            }
            else
            {
            this.recentSearches.add(
                [
                    type,
                    cachedTerms,
                    msg.resultSet.length,
                    this.resultTime,
                    msg.canGetMoreData
            ]);
            }
        });
    }

    public runRecentQuery (index : number) : void
    {
        const recent : HistoricalSearch = this.recentSearches.list()[index];
        if (recent)
        {
            const [type, query, count, _] : HistoricalSearch = recent;
            const remagickedQuery : SearchTerm[] =
              query.map((t : SearchTerm) => {
                if (t.magicSource)
                {
                  return t.magicSource[0].transform(t.name, t.magicSource[1]);
                }
                return t;
              });
            this.runQuery(type, remagickedQuery, count);
        }
        else
        {
            throw new Error(`Could not find recent search with index ${index}`);
        }
    }

    public clearRecents () : void
    {
      this.recentSearches.clear();
    }

    public clearResults () : void
    {
        this.results = null;
        this.resultTime = null;
        this.moreResultsAvailable = false;
        this.lastResultOpened = null;
    }

    public requestMoreResults () : void
    {
        if (this.moreResultsAvailable === true)
        {
            const requestStart : Date = new Date();
            this.messaging.converse(
                new AdditionalSearchResultsRequest()
            ).subscribe(
                (msg : SubjectContextSearchResponse) => {
                    msg.resultSet.forEach((r : Result) => this.results.push(r));
                    this.moreResultsAvailable = msg.canGetMoreData;

                    this.queryTime = requestStart;
                    this.resultTime = new Date();
                });
        }
        else
        {
            return console.error("No additional results available!");
        }
    }

    private getRef (result : Result) : SubjectReference
    {
        return {
            id: result.recordId,
            host: this,
            type: "search",
            request: new SubjectContextSearchResultRequest(result.recordId),
            priority: 50
        };
    }

    public open (
        result : Result,
        navigate : boolean = true,
        target? : Algorithm
    ) : Observable<Subject>
    {
        this.lastResultOpened = result;
        const subject : Subject = this.subjects.fromReference(
            this.getRef(result));
        if (target)
        {
            subject.initialAlgorithmTarget = target;
        }
        // subject.summary = result.columns;
        return this.subjects.open(subject, navigate);
    }

    // public openDefault (
    //   result : Result,
    //   navigate : boolean = true
    // ) {
    //   const target : Algorithm | null =
    //   this.algorithms.defaultForOpen(this.resultContextType);
    //   return this.open()
    // }

    private static readonly HCB_EXTERNAL_ID_FIELD : string = "case identifier";
    private getIdentifierForType (sct : SubjectContextType) : string
    {
        if (sct === "healthcare benefits")
        {
            return SearchService.HCB_EXTERNAL_ID_FIELD;
        }
        throw new Error(
            "Can only load healthcare benefits records using external IDs!");
    }

    private buildQueryForExternal (
        sct : SubjectContextType,
        idStepName : string,
        id : string
    ) : SearchTerm
    {
        const root : SearchRoot = this.rootByType.get(sct);
        assert(root !== undefined, "Could not find root for SCT");
        const target : SearchNode = root.retrieve([idStepName]);
        assert(
            target !== null,
            "Could not retrieve node for external identifier");
        assert(target.canSearchHere(), "Cannot search at external ID node");
        return {
            name: idStepName,
            operator: "=",
            value: id
        };
    }

    private getByExternalID (
        typeString : SubjectContextType,
        idString : string) : void
    {
        this.ready.first((r : boolean) => r).subscribe(() => {
        // we know it's ready by the first filter
            const term : SearchTerm = this.buildQueryForExternal(
                typeString, this.getIdentifierForType(typeString), idString);
            this.messaging.converse(
                new SubjectContextSearchRequest(
                    typeString,
                    [term],
                    []
                ), (error : AcknowledgmentResponse) => {
                    this.alerts.warn(oneline`
                        The server could not locate a record for that ID.`);
                }
            ).subscribe((msg : SubjectContextSearchResponse) => {
                if (msg.resultSet.length === 0)
                {
                    this.alerts.warn(oneline`
                        The server could not locate a record for that ID.`);
                }
                else if (msg.resultSet.length > 1)
                {
                    this.alerts.warn(oneline`
                        The server found multiple records with that ID.`);
                }
                else
                {
                    // SUCCESS
                    const result : Result = msg.resultSet[0]!!;
                    this.open(result, true).subscribe(); // TODO: target?
                }
            });
        });
    }

    public vendorABC_navigateToHCBRecord (id : string) : void
    {
        this.getByExternalID("healthcare benefits", id);
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/search/search.service.ts